import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Route } from '../../types';
import { apiService } from '../../services/api';

interface RoutesState {
  routes: Route[];
  currentRoute: Route | null;
  loading: boolean;
  error: string | null;
}

const initialState: RoutesState = {
  routes: [],
  currentRoute: null,
  loading: false,
  error: null,
};

// Тип для API ответа
interface RoutesApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Route[];
}

// Async thunks
export const fetchRoutes = createAsyncThunk(
  'routes/fetchRoutes',
  async (params?: Record<string, any>): Promise<RoutesApiResponse | Route[]> => {
    console.log('fetchRoutes thunk: calling API with params:', params);
    try {
      const routes = await apiService.getRoutes(params);
      console.log('fetchRoutes thunk: success:', routes);
      return routes;
    } catch (error) {
      console.error('fetchRoutes thunk: error:', error);
      throw error;
    }
  }
);

export const fetchRoute = createAsyncThunk(
  'routes/fetchRoute',
  async (id: number) => {
    return await apiService.getRoute(id);
  }
);

export const createRoute = createAsyncThunk(
  'routes/createRoute',
  async (data: Partial<Route>) => {
    return await apiService.createRoute(data);
  }
);

export const updateRoute = createAsyncThunk(
  'routes/updateRoute',
  async ({ id, data }: { id: number; data: Partial<Route> }) => {
    return await apiService.updateRoute(id, data);
  }
);

export const deleteRoute = createAsyncThunk(
  'routes/deleteRoute',
  async (id: number) => {
    await apiService.deleteRoute(id);
    return id;
  }
);

export const copyRoute = createAsyncThunk(
  'routes/copyRoute',
  async (id: number) => {
    return await apiService.copyRoute(id);
  }
);

export const shiftDates = createAsyncThunk(
  'routes/shiftDates',
  async ({ id, daysShift }: { id: number; daysShift: number }) => {
    return await apiService.shiftDates(id, daysShift);
  }
);

const routesSlice = createSlice({
  name: 'routes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentRoute: (state, action: PayloadAction<Route | null>) => {
      state.currentRoute = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch routes
      .addCase(fetchRoutes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoutes.fulfilled, (state, action) => {
        state.loading = false;
        // API может возвращать объект с полем results или массив напрямую
        if ('results' in action.payload && Array.isArray(action.payload.results)) {
          state.routes = action.payload.results;
        } else if (Array.isArray(action.payload)) {
          state.routes = action.payload;
        } else {
          state.routes = [];
        }
      })
      .addCase(fetchRoutes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Ошибка загрузки маршрутов';
      })
      // Fetch single route
      .addCase(fetchRoute.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoute.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRoute = action.payload;
      })
      .addCase(fetchRoute.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Ошибка загрузки маршрута';
      })
      // Create route
      .addCase(createRoute.fulfilled, (state, action) => {
        state.routes.unshift(action.payload);
      })
      // Update route
      .addCase(updateRoute.fulfilled, (state, action) => {
        const index = state.routes.findIndex(route => route.id === action.payload.id);
        if (index !== -1) {
          state.routes[index] = action.payload;
        }
        if (state.currentRoute?.id === action.payload.id) {
          state.currentRoute = action.payload;
        }
      })
      // Delete route
      .addCase(deleteRoute.fulfilled, (state, action) => {
        state.routes = state.routes.filter(route => route.id !== action.payload);
        if (state.currentRoute?.id === action.payload) {
          state.currentRoute = null;
        }
      })
      // Copy route
      .addCase(copyRoute.fulfilled, (state, action) => {
        state.routes.unshift(action.payload);
      })
      // Shift dates
      .addCase(shiftDates.fulfilled, (state, action) => {
        const index = state.routes.findIndex(route => route.id === action.payload.id);
        if (index !== -1) {
          state.routes[index] = action.payload;
        }
        if (state.currentRoute?.id === action.payload.id) {
          state.currentRoute = action.payload;
        }
      });
  },
});

export const { clearError, setCurrentRoute } = routesSlice.actions;
export default routesSlice.reducer; 